/**
 * Adapts the day-0 plan from GET /api/day0/state into what the renderers take.
 *
 * THERE ARE NO COMPOSITION RULES IN THIS FILE, DELIBERATELY.
 *
 * architecture.md: the plan a participant is shown is composed SERVER-side and
 * the client renders what it is given. Which instruments someone sees is
 * study-protocol logic — it gates on demographics, drops items whose format is
 * a blocking stub, and orders by block then questionnaire then item. Two
 * implementations of that can disagree about whether a participant is asked
 * about suicidal ideation, and only one of the two would be in the export.
 *
 * This file previously composed the plan from config. That code is gone: no
 * gating, no block_order, no item.order sorting, no blocking-stub filter. If
 * you find yourself re-adding any of them here, the rule moved to the wrong
 * side of the wire.
 *
 * What stays client-side: which section to resume at, and rendering.
 */

/**
 * @param {object} state GET /api/day0/state
 * @returns {Array<{questionnaire, items}>} in the server's order, never re-sorted
 */
export function sectionsFromState(state) {
  return (state?.sections ?? []).map((section) => ({
    questionnaire: {
      id: section.questionnaire_id,
      title: section.title,
      block: section.block,
      timeframe: section.timeframe,
      // Both of these are the server's, passed through untouched. griddable is
      // still verified against the items by canGrid before a grid is drawn.
      instruction: section.instruction,
      griddable: section.griddable === true,
    },
    // Per-section completeness, taken from the server and NEVER recomputed.
    // required_outstanding is a list of item ids, so it both gates Continue and
    // says exactly which questions are missing.
    itemCount: section.item_count ?? (section.items ?? []).length,
    answeredCount: section.answered_count ?? 0,
    requiredOutstanding: section.required_outstanding ?? [],
    complete: section.complete === true,
    items: (section.items ?? []).map((item) => ({
      item_id: item.item_id,
      prompt: item.prompt,
      response_format: item.response_format,
      is_safety: item.is_safety === true,
    })),
  }))
}

/**
 * The three item states, from the server and only from the server.
 *
 * required is the DEFAULT and is never written down: an item is required unless
 * its id appears here. There is deliberately no `optional: false` anywhere in
 * the system, so nothing may infer optionality from any other field.
 *
 * A gated-out item is simply absent from the plan — never asked, so it cannot
 * be skipped and there is nothing here to handle.
 */
export function optionalIdsFromState(state) {
  return new Set(state?.optional_item_ids ?? [])
}

/** Item ids the participant must answer, i.e. everything not declared optional. */
export function requiredIdsFromState(state, sections) {
  const optional = optionalIdsFromState(state)
  const required = new Set()
  for (const section of sections) {
    for (const item of section.items) {
      if (!optional.has(item.item_id)) required.add(item.item_id)
    }
  }
  return required
}

/**
 * Answers already on the server, keyed by item id.
 *
 * Resume is a QUERY, not a draft: this is the server telling us what it holds,
 * values included, so going back through a finished section shows what was
 * actually chosen rather than a blank control. Nothing is stored on the device.
 */
export function answersFromState(state) {
  const answers = {}
  for (const section of state?.sections ?? []) {
    for (const item of section.items ?? []) {
      if (item.answered) {
        answers[item.item_id] = { value: item.value ?? null, text: item.free_text ?? '' }
      }
    }
  }
  return answers
}

/** Flat item list in the server's order, for counting and cross-checks. */
export function flattenPlan(sections) {
  return sections.flatMap((section) => section.items)
}

/**
 * Where to drop the participant back in: the section the server says they are
 * on, current_section_id.
 *
 * This used to be an involved guess — 'the furthest section holding an answer'
 * — written to work around a rule that no longer exists. While any non-safety
 * item could be left blank, nothing derivable from the answers could say how
 * far someone had actually got, and every honest option was bad.
 *
 * No skipping removes the ambiguity outright: the first incomplete section IS
 * where they are, and the server names it. The guesswork is deleted rather than
 * kept just in case, because a second opinion about position is exactly how the
 * client and server drifted apart the first time.
 *
 * Returns the section count when every section is complete.
 */
export function resumeSectionIndex(sections, currentSectionId) {
  if (!sections.length) return 0
  if (!currentSectionId) return sections.length // nothing outstanding anywhere
  const index = sections.findIndex((s) => s.questionnaire.id === currentSectionId)
  return index === -1 ? 0 : index
}
